// ============================================================
// TMR Portal Admin — o histórico de uma taxa
// ============================================================
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { bandLabel } from '../../data/rateFormat';
import { useRateHistory } from '../../data/rates/queries';
import {
  CHANGE_KIND_LABEL,
  CHANGE_ORIGIN_LABEL,
  actorLabel,
  formatInstant,
  valueTransition,
} from '../../data/rates/history';
import { rateTitle } from '../../data/rates/status';
import type { CompanyRate } from '../../data/rates/contract';

/** Props for {@link RateHistoryModal}. */
interface RateHistoryModalProps {
  companyId: string;
  /** The rate whose history to show. */
  rate: CompanyRate;
  onClose: () => void;
}

/**
 * What happened to one rate: who changed it, from which value to which, and what caused it.
 *
 * <b>It exists because the migration would otherwise make auditing worse than the system it replaces.</b>
 * Today a coupon writes `sc_log`, and so does the legacy's own temporary-window pair — user, IP, the
 * original value, the temporary one and the window. After the migration there would be nothing in the V2
 * and nothing usable in the legacy, whose rows the export stamps `modificado_por = 1` (`15` D-A11).
 *
 * The acceptance criterion is precisely this screen: seeing the previous value, the new one, the operator
 * and the origin **without opening the database**. An audit trail that needs a SQL client is one that will
 * not be consulted during the incident it was written for.
 */
export function RateHistoryModal({ companyId, rate, onClose }: RateHistoryModalProps) {
  const history = useRateHistory(companyId, rate.id);

  return (
    <ModalShell
      title="Histórico da taxa"
      lead={`${rateTitle(rate)} · ${bandLabel(rate)}`}
      onClose={onClose}
    >
      {history.isPending ? (
        <Skeleton rows={3} />
      ) : history.isError ? (
        <ErrorState error={history.error} onRetry={() => void history.refetch()} />
      ) : history.data.items.length === 0 ? (
        <EmptyState
          message="Nenhuma mudança registrada nesta taxa."
          // The trail starts when R4 ships, so a rate that predates it has a real history that simply was
          // not recorded. Saying so beats implying the rate never moved.
          hint="O registro passou a ser gravado a partir desta versão — mudanças anteriores não têm linha aqui."
        />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {history.data.items.map((entry) => {
            const values = valueTransition(entry, rate.rateTypeId);

            return (
              <div
                key={entry.id}
                style={{
                  padding: '12px 14px',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 550 }}>
                    {CHANGE_KIND_LABEL[entry.kind]}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>
                    {formatInstant(entry.occurredAt)}
                  </span>
                </div>

                {values && (
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 14,
                      marginTop: 6,
                    }}
                  >
                    {values}
                  </div>
                )}

                <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 6 }}>
                  {actorLabel(entry)} · {CHANGE_ORIGIN_LABEL[entry.origin]}
                </div>

                {entry.reason && (
                  <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', marginTop: 4 }}>
                    “{entry.reason}”
                  </div>
                )}
              </div>
            );
          })}

          {/* The trail covers this system only, and saying so is the honest bound on what it proves. */}
          <Notice tone="neutral">
            O registro cobre as mudanças feitas por aqui e as trazidas do sistema legado. Uma edição feita
            direto no back-office antigo aparece como “trazido do sistema legado”, na sincronização
            seguinte.
          </Notice>
        </div>
      )}
    </ModalShell>
  );
}
